import type { Location } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { SCROLL_THRESHOLD } from './constants';
import { TransformedChannel } from '@/stores/channelStore';

// 扩展 ChannelTab 类型，添加 path 属性
type NavigationItem = TransformedChannel & {
  path: string;
  label: string;
};

/**
 * 根据当前路径找到对应的导航项索引
 */
export function getCurrentNavIndex(navItems: NavigationItem[], location: Location): number {
  const exactMatch = navItems.findIndex((item) => item.path === location.pathname);

  if (exactMatch >= 0) {
    return exactMatch;
  }

  const parentMatch = navItems.findIndex((item) => {
    if (item.path === '/') {
      return false;
    }
    const startsWithPath = location.pathname.startsWith(`${item.path}/`);
    const exactPathMatch = location.pathname === item.path;
    return startsWithPath || exactPathMatch;
  });

  return parentMatch >= 0 ? parentMatch : 0;
}

/**
 * 计算导航项需要滚动的距离
 * 优化：确保获焦元素不会被滚动到遮罩区域
 */
export function calculateNavItemScroll(navFocusKey: string, container: HTMLElement): number | null {
  const focusedElement = container.querySelector(`[data-focus-key="${navFocusKey}"]`) as HTMLElement | null;
  if (!focusedElement) {
    return null;
  }

  const containerWidth = container.clientWidth;
  const currentScrollLeft = container.scrollLeft;

  // 使用 getBoundingClientRect 获取相对于视口的位置
  const containerRect = container.getBoundingClientRect();
  const elementRect = focusedElement.getBoundingClientRect();

  // 计算元素相对于容器左边的距离（考虑滚动）
  const elementPositionInContainer = elementRect.left - containerRect.left + currentScrollLeft;
  const elementOffsetLeft = elementPositionInContainer;
  const elementOffsetRight = elementPositionInContainer + elementRect.width;

  // 遮罩区域的阈值（15%的渐变区域 + 额外的安全边距）
  const maskThreshold = containerWidth * 0.15;
  const safeMargin = 20; // 额外的安全边距
  const leftSafeZone = maskThreshold + safeMargin;
  const rightSafeZone = containerWidth - maskThreshold - safeMargin;

  // 检查元素是否在安全可见区域内（避开遮罩区域）
  const isLeftSafe = elementOffsetLeft >= currentScrollLeft + leftSafeZone;
  const isRightSafe = elementOffsetRight <= currentScrollLeft + rightSafeZone;
  const isInSafeZone = isLeftSafe && isRightSafe;

  // 如果在安全区域内，不需要滚动
  if (isInSafeZone) return null;

  // 计算新的滚动位置，确保元素在安全区域内
  if (elementOffsetRight > currentScrollLeft + rightSafeZone) {
    // 元素右侧超出安全区域，向右滚动
    const newScrollLeft = elementOffsetRight - rightSafeZone;
    return Math.min(newScrollLeft, container.scrollWidth - containerWidth);
  }

  if (elementOffsetLeft < currentScrollLeft + leftSafeZone) {
    // 元素左侧超出安全区域，向左滚动
    const newScrollLeft = Math.max(0, elementOffsetLeft - leftSafeZone);
    return newScrollLeft;
  }

  return null;
}

/**
 * 计算导航滚动状态
 */
export function calculateNavScrollState(container: HTMLElement) {
  const { scrollLeft, scrollWidth, clientWidth } = container;

  // 检测是否有溢出内容
  const hasOverflow = scrollWidth > clientWidth;

  // 左侧遮挡检测：scrollLeft > 0 说明有内容被滚动到左侧不可见区域
  const hasLeftOverflow = scrollLeft > SCROLL_THRESHOLD;

  // 右侧遮挡检测
  const hasRightOverflow = hasOverflow && scrollLeft + clientWidth < scrollWidth - SCROLL_THRESHOLD;

  return {
    scrollLeft,
    showLeftGradient: hasLeftOverflow,
    showRightGradient: hasRightOverflow,
  };
}

/**
 * 更新高亮层位置和尺寸
 * 优化：确保高亮层不被遮罩遮挡
 */
export function calculateHighlightPosition(navFocusKey: string, container: HTMLElement | null) {
  if (!container || !navFocusKey) {
    return { visible: false, left: 0, width: 0, zIndex: 1 };
  }

  const focusedElement = container.querySelector(`[data-focus-key="${navFocusKey}"]`) as HTMLElement | null;

  if (!focusedElement) {
    return { visible: false, left: 0, width: 0, zIndex: 1 };
  }

  // 获取滚动容器（navContent的父容器）
  const scrollContainer = container.parentElement;
  if (!scrollContainer) {
    return { visible: false, left: 0, width: 0, zIndex: 1 };
  }

  // 获取元素相对于容器的位置
  const containerRect = container.getBoundingClientRect();
  const elementRect = focusedElement.getBoundingClientRect();
  const scrollContainerRect = scrollContainer.getBoundingClientRect();

  // 计算相对于 navContent 容器的 left 位置
  const left = elementRect.left - containerRect.left;
  const { width } = elementRect;

  // 检查是否需要提高z-index以避免被遮罩遮挡
  const { scrollLeft } = scrollContainer;
  const scrollContainerWidth = scrollContainer.clientWidth;

  // 计算元素在滚动容器中的相对位置
  const elementLeftInScroll = elementRect.left - scrollContainerRect.left;
  const elementRightInScroll = elementLeftInScroll + width;

  // 遮罩区域的阈值（15%的渐变区域）
  const maskThreshold = scrollContainerWidth * 0.15;

  // 检查是否在遮罩区域
  const isInLeftMask = elementLeftInScroll < maskThreshold && scrollLeft > SCROLL_THRESHOLD;
  const isInRightMask =
    elementRightInScroll > scrollContainerWidth - maskThreshold &&
    scrollLeft + scrollContainerWidth < scrollContainer.scrollWidth - SCROLL_THRESHOLD;

  // 如果在遮罩区域，提高z-index确保可见性
  const zIndex = isInLeftMask || isInRightMask ? 10 : 1;

  return {
    left,
    width,
    visible: true,
    zIndex,
  };
}

/**
 * 获取 mask-image 样式类名
 */
export function getMaskImageClassName(
  showLeftGradient: boolean,
  showRightGradient: boolean,
  styles: Record<string, string>
): string {
  if (showLeftGradient && showRightGradient) {
    return styles.showBothMask;
  }
  if (showLeftGradient) {
    return styles.showLeftMask;
  }
  if (showRightGradient) {
    return styles.showRightMask;
  }
  return '';
}

/**
 * 获取 mask-image 内联样式（作为备用方案）
 */
export function getMaskImageInlineStyle(showLeftGradient: boolean, showRightGradient: boolean): CSSProperties {
  if (showLeftGradient && showRightGradient) {
    return { WebkitMaskImage: bothGradient, maskImage: bothGradient };
  }

  if (showLeftGradient) {
    return { WebkitMaskImage: leftGradient, maskImage: leftGradient };
  }

  if (showRightGradient) {
    return { WebkitMaskImage: rightGradient, maskImage: rightGradient };
  }

  return {};
}

// 渐变样式常量
const bothGradient =
  'linear-gradient(to right, ' +
  'rgba(0, 0, 0, 0) 0%, ' +
  'rgba(0, 0, 0, 0.3) 5%, ' +
  'rgba(0, 0, 0, 1) 15%, ' +
  'rgba(0, 0, 0, 1) 85%, ' +
  'rgba(0, 0, 0, 0.3) 95%, ' +
  'rgba(0, 0, 0, 0) 100%)';

const leftGradient =
  'linear-gradient(to right, ' + 'rgba(0, 0, 0, 0) 0%, ' + 'rgba(0, 0, 0, 0.3) 5%, ' + 'rgba(0, 0, 0, 1) 15%)';

const rightGradient =
  'linear-gradient(to right, ' + 'rgba(0, 0, 0, 1) 85%, ' + 'rgba(0, 0, 0, 0.3) 95%, ' + 'rgba(0, 0, 0, 0) 100%)';
